<template>
  <Popup
    :model-value="modelValue"
    popup-header="You’re invited to become a Trusted Sitter"
    popup-size="lg"
    header-class="text-h3 sm:text-h2 sm:pt-16"
    custom-classes="sm:!max-w-[480px] sm:!px-40 sm:!pb-40"
    :close-on-outside-click="false"
    :hide-close-button="true"
    @close="onRemindLater"
  >
    <div class="flex flex-col items-start sm:flex-row sm:items-center gap-12 mt-8 mb-24">
      <TrustedSitterBannerIcon :width="148" :height="30" aria-hidden="true" />
      <Pill :color="PillColor.brand" class="!text-brand">EXCLUSIVE INVITATION</Pill>
    </div>

    <div class="space-y-16 mb-16">
      <p class="text-body text-grey-body mb-0">
        You’ve been selected for early access to Sittercity’s new Trusted Caregiver Program! It’s designed to recognize and
        support caregivers who continually meet a higher standard for trust, reliability, and professionalism.
      </p>
      <p class="text-body text-grey-body mb-0">
        Trusted Sitters earn a profile badge, which helps you stand out and builds credibility that can open the door to more
        connections and opportunities. To become a Trusted Sitter, you’ll need to complete a few required steps.
      </p>
      <p class="text-strong-body text-grey-body mb-0">Interested? Accept your invitation to get started.</p>
    </div>

    <a
      href="https://www.sittercity.com/trusted-caregiver-program"
      class="text-body text-primary inline-flex items-center gap-8 primary-focus mb-16"
      target="_blank"
    >
      Learn more about the Trusted Caregiver Program
      <NewTabIcon :width="16" :height="16" aria-hidden="true" />
    </a>

    <p v-if="errorMessage" class="text-body text-critical mb-16" role="alert">{{ errorMessage }}</p>

    <div class="flex flex-col items-center pt-8">
      <div class="flex justify-center gap-16">
        <Button
          value="I decline"
          variant="outlined"
          size="medium"
          :loading="submitting === 'DECLINE'"
          :disabled="Boolean(submitting)"
          @click="onDecline"
        />
        <Button
          value="I accept"
          variant="primary"
          size="medium"
          :loading="submitting === 'ACCEPT'"
          :disabled="Boolean(submitting)"
          @click="onAccept"
        />
      </div>
      <button
        type="button"
        class="text-body text-grey-body mt-16 primary-focus"
        :disabled="Boolean(submitting)"
        @click="onRemindLater"
      >
        Remind me later
      </button>
    </div>
  </Popup>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Popup from '@/components/popup/Popup.vue';
import Button from '@/components/button/Button.vue';
import Pill from '@/components/pill/Pill.vue';
import TrustedSitterBannerIcon from '@/graphics/icons/TrustedSitterBannerIcon.vue';
import NewTabIcon from '@/graphics/icons/NewTabIcon.vue';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import {
  RESPOND_TO_TRUSTED_CAREGIVER_INVITE,
  type TrustedCaregiverInviteResponse,
} from '@/graphql/provider/trusted_caregiver/mutations';
import { snoozeTrustedCaregiverInvite } from './trustedCaregiverInviteSnooze';
import { PillColor } from '@/components/pill/Pill.vue';

defineOptions({ name: 'TrustedCaregiverPopup' });

const props = defineProps<{
  modelValue: boolean;
  accountId: string | number;
}>();

const emit = defineEmits<{
  accept: [];
  decline: [];
  remindLater: [];
}>();

const submitting = ref<TrustedCaregiverInviteResponse | null>(null);
const errorMessage = ref('');
const remindingLater = ref(false);
// Popup emits `close` whenever it shuts (including when the parent flips modelValue
// after accept/decline). Only X, Escape, and "Remind me later" should snooze.
const snoozeOnClose = ref(true);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      submitting.value = null;
      errorMessage.value = '';
      remindingLater.value = false;
      snoozeOnClose.value = true;
    }
  }
);

function onRemindLater() {
  if (submitting.value || remindingLater.value) return;
  if (!snoozeOnClose.value) {
    snoozeOnClose.value = true;
    return;
  }
  remindingLater.value = true;
  snoozeTrustedCaregiverInvite(props.accountId);
  emit('remindLater');
}

async function onDecline() {
  if (submitting.value) return;

  const succeeded = await respondToInvite('DECLINE');
  if (!succeeded) return;

  snoozeOnClose.value = false;
  emit('decline');
}

async function onAccept() {
  if (submitting.value) return;

  const succeeded = await respondToInvite('ACCEPT');
  if (!succeeded) return;

  snoozeOnClose.value = false;
  emit('accept');
}

async function respondToInvite(response: TrustedCaregiverInviteResponse): Promise<boolean> {
  submitting.value = response;
  errorMessage.value = '';

  try {
    const { data } = await getSharedApolloClient().mutate({
      mutation: RESPOND_TO_TRUSTED_CAREGIVER_INVITE,
      variables: { response },
    });

    if (!data?.respondToTrustedCaregiverInvite) {
      throw new Error('Trusted Caregiver invite response was not recorded');
    }

    // Accept must return an onboarding status; decline intentionally returns null.
    if (response === 'ACCEPT' && !data.respondToTrustedCaregiverInvite.status) {
      throw new Error('Trusted Caregiver invite was not accepted');
    }

    return true;
  } catch (error) {
    console.error('Unable to respond to Trusted Caregiver invite', error);
    errorMessage.value = 'Something went wrong. Please try again.';
    return false;
  } finally {
    submitting.value = null;
  }
}
</script>
